/**
 * SCENE 02: THE MONOLITH OF RESISTANCE (Antiquity // 3000 BCE)
 * 50-ton granite slab blocking mountain pass. Requires sustained steady pressure to open.
 */

import { ISceneLifecycle } from './ISceneLifecycle';
import { CameraState } from '../renderer/CameraSpline';
import { EventBus, EventType } from '../core/EventBus';
import { StateStore, StateBitmask } from '../state/Store';
import { MATERIALS } from '../physics/VerletKernel';

export class Scene02_Monolith implements ISceneLifecycle {
  public id = 1;
  public name = 'The Monolith of Resistance';
  public era = 'Antiquity (3000 BCE)';

  private eventBus: EventBus;
  private store: StateStore;
  private slabOffset: number = 0; // 0 = closed, 1 = fully open
  private slabVelocity: number = 0;
  private isUnlocked: boolean = false;
  private unsubscribeList: Array<() => void> = [];

  constructor() {
    this.eventBus = EventBus.getInstance();
    this.store = StateStore.getInstance();
  }

  public async preload(): Promise<void> { return Promise.resolve(); }
  public mount(): void {}

  public activate(): void {
    const unsubMove = this.eventBus.subscribe(EventType.INPUT_POINTER_MOVE, (payload) => {
      const vel = payload.velocity || 0;
      // Fast swipes fail due to heavy mass inertia (50 tons)
      // Only sustained steady pressure (velocity between 10 and 150) moves the heavy stone
      if (vel > 5 && vel < 180) {
        this.slabVelocity += 0.008;
        this.eventBus.emit(EventType.AUDIO_TRIGGER_FRICTION, payload.x, payload.y, 0, 0, vel);
      }
    });

    this.unsubscribeList.push(unsubMove);
  }

  public update(dt: number): void {
    // Heavy mass friction damping
    const friction = MATERIALS.STONE.friction;
    this.slabVelocity *= (1 - friction * dt * 2);

    this.slabOffset = Math.min(1.0, this.slabOffset + this.slabVelocity * dt);

    if (this.slabOffset >= 0.95 && !this.isUnlocked) {
      this.isUnlocked = true;
      this.store.setFlag(StateBitmask.MONOLITH_UNLOCKED, true);
      this.eventBus.emit(
        EventType.ACCESSIBILITY_ANNOUNCE,
        0, 0, 0, 0, 0, 1, 1,
        '50-ton granite monolith glides open under steady pressure, unlocking the mountain pass.'
      );
    }
  }

  public render(ctx: CanvasRenderingContext2D, width: number, height: number, _camera: CameraState): void {
    ctx.save();
    
    // Render Mountain Pass Canyon Walls
    ctx.fillStyle = '#141210';
    ctx.fillRect(0, height * 0.2, width * 0.35, height * 0.6);
    ctx.fillRect(width * 0.65, height * 0.2, width * 0.35, height * 0.6);

    // Render Sliding Heavy Granite Slab
    const slabWidth = width * 0.32;
    const slabHeight = height * 0.58;
    const currentX = (width / 2 - slabWidth / 2) + this.slabOffset * (width * 0.35);
    const currentY = height * 0.21;

    // Glowing Friction Grooves
    if (this.slabOffset > 0.02) {
      ctx.shadowColor = 'rgba(230, 120, 50, 0.9)';
      ctx.shadowBlur = 12;
      ctx.fillStyle = 'rgba(240, 140, 60, 0.6)';
      ctx.fillRect(width / 2 - slabWidth / 2, currentY + slabHeight - 6, this.slabOffset * (width * 0.35), 6);
      ctx.shadowBlur = 0;
    }

    // Monolith Body
    ctx.fillStyle = '#22201d';
    ctx.strokeStyle = '#3a3630';
    ctx.lineWidth = 2;
    ctx.fillRect(currentX, currentY, slabWidth, slabHeight);
    ctx.strokeRect(currentX, currentY, slabWidth, slabHeight);

    // Relief Text on Monolith
    ctx.font = '600 13px "Inter", monospace';
    ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(180, 170, 160, 0.5)';
    ctx.fillText('MONOLITH OF RESISTANCE', currentX + slabWidth / 2, currentY + slabHeight / 2 - 10);
    ctx.font = '400 11px monospace';
    ctx.fillText('[ SUSTAIN SLOW PRESSURE TO PUSH ]', currentX + slabWidth / 2, currentY + slabHeight / 2 + 15);

    ctx.restore();
  }

  public deactivate(): void {
    this.unsubscribeList.forEach(fn => fn());
    this.unsubscribeList = [];
  }
  public unmount(): void {}
  public dispose(): void {}

  public getProgress(): number {
    return this.slabOffset;
  }
}
